// Meta-list page reached via MetaNav. Pivots over a dimension (department,
// manager, location, etc.) and shows one row per value with users / risk
// / click summary. Selecting a row drills into the scoped Sims view.
//
// Calculations: precomputed aggregates (DEPTS, LOCS, ...) when present;
// otherwise derived in-component by grouping USERS by the requested key
// and reducing SEND_LOG to clicks per group.
//
// Postgres mapping: every supported dimension should have a matching
// nightly materialised view; see DeptPage header.
// Integration hook: `useDimensionQuery({ dimension, period, scope })`.
import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MainCard from '../../../components/cards/MainCard';
import { DEPTS, LOCS, CITIES, COUNTRIES, COMPANIES, STATES, COURSES, USERS, SEND_LOG, COURSE_SEND_LOG, DATA_EPOCH } from '../data';
import { buildCourseRows } from './AllCoursesPage';
import useDashboardData, { logInPeriod } from '../hooks/useDashboardData';
import { riskColor } from '../helpers';
import { RowViewButton } from './DataTable';
import WidgetState from './WidgetState';

// Derive a metadata bucket array for any field that lives on the USER
// record but isn't pre-aggregated (line manager, etc.). Mirrors the shape
// produced by aggregateBy() in data.js so MetaListPage can render it with
// the same column set as DEPTS / LOCS.
function aggregateUserField(field) {
    const buckets = {};
    for (const u of USERS) {
        const key = u[field];
        if (!key) continue;
        if (!buckets[key]) buckets[key] = { name: key, riskSum: 0, users: 0, completionSum: 0, clickSum: 0, sendCount: 0, lvCount: { high: 0, med: 0, low: 0 } };
        buckets[key].riskSum += u.risk;
        buckets[key].users++;
        buckets[key].completionSum += u.comp ?? 0;
        if (u.lv) buckets[key].lvCount[u.lv]++;
    }
    return Object.values(buckets).map((b) => ({
        name: b.name,
        users: b.users,
        risk: b.users === 0 ? 0 : Math.round(b.riskSum / b.users),
        completion: b.users === 0 ? 0 : Math.round(b.completionSum / b.users),
        click: 0, // sends don't carry manager so click rate isn't computable for this dimension
        lv: b.lvCount.high > b.lvCount.low ? 'high' : b.lvCount.med > b.lvCount.low ? 'med' : 'low',
    }));
}

// Same idea for fields that only live on the send log (comms type, etc.).
function aggregateLogField(field) {
    const buckets = {};
    for (const e of SEND_LOG) {
        const key = e[field];
        if (!key) continue;
        if (!buckets[key]) buckets[key] = { name: key, sendCount: 0, clickSum: 0 };
        buckets[key].sendCount++;
        if (e.status === 'clicked' || e.status === 'clicked_2nd') buckets[key].clickSum++;
    }
    return Object.values(buckets).map((b) => ({
        name: b.name,
        users: b.sendCount,
        risk: 0,
        completion: 0,
        click: b.sendCount === 0 ? 0 : Math.round((b.clickSum / b.sendCount) * 100),
        lv: b.clickSum / Math.max(1, b.sendCount) >= 0.25 ? 'high' : b.clickSum / Math.max(1, b.sendCount) >= 0.15 ? 'med' : 'low',
    }));
}

// Log field each dimension groups on.
const LOG_FIELD = { department: 'dept', location: 'location', city: 'city', country: 'country', company: 'company', state: 'state', commsType: 'commsType' };
const isClick = (e) => e.status === 'clicked' || e.status === 'clicked_2nd' || e.status === 'attachment';

// Period figures for the local rows (BD-1050): click rate and completion from the sends
// in the selected period. Users and risk keep their source values (risk is the fixed
// 6-month score). Courses and users re-derive their own counts the same way.
function windowedSource(metaType, source, range) {
    const sims = logInPeriod(SEND_LOG, range);
    const courses = logInPeriod(COURSE_SEND_LOG, range);
    const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
    if (metaType === 'course') {
        return buildCourseRows(courses).map((c) => ({ ...(source.find((s) => s.name === c.name) || {}), ...c, pct: c.completionPct }));
    }
    if (metaType === 'user') {
        const by = new Map();
        const get = (email) => by.get(email) || by.set(email, { sent: 0, clicks: 0, cSent: 0, cDone: 0 }).get(email);
        for (const e of sims) { const u = get(e.email); u.sent += 1; if (isClick(e)) u.clicks += 1; }
        for (const e of courses) { const u = get(e.email); u.cSent += 1; if (e.dateCompleted) u.cDone += 1; }
        return source.map((u) => { const w = by.get(u.email); return { ...u, clicks: w ? w.clicks : 0, comp: w ? pct(w.cDone, w.cSent) : 0 }; });
    }
    const managerOf = metaType === 'manager' ? new Map(USERS.map((u) => [u.email, u.manager])) : null;
    const keysOf = (e) => {
        if (managerOf) return [managerOf.get(e.email)].filter(Boolean);
        const v = e[LOG_FIELD[metaType]];
        return Array.isArray(v) ? v : v ? [v] : [];
    };
    if (!managerOf && !LOG_FIELD[metaType]) return source;
    const agg = new Map();
    const get = (k) => agg.get(k) || agg.set(k, { sent: 0, clicks: 0, cSent: 0, cDone: 0 }).get(k);
    for (const e of sims) for (const k of keysOf(e)) { const a = get(k); a.sent += 1; if (isClick(e)) a.clicks += 1; }
    for (const e of courses) for (const k of keysOf(e)) { const a = get(k); a.cSent += 1; if (e.dateCompleted) a.cDone += 1; }
    const rows = source.map((r) => { const a = agg.get(r.name); return { ...r, click: a ? pct(a.clicks, a.sent) : 0, completion: a ? pct(a.cDone, a.cSent) : 0 }; });
    if (metaType === 'commsType') return rows.filter((r) => agg.has(r.name)).map((r) => ({ ...r, users: agg.get(r.name).sent }));
    return rows;
}

// Recomputed at access time so they track the live hydrated data.
const managers = () => aggregateUserField('manager');
const commsTypes = () => aggregateLogField('commsType');

// Each meta type defines its source data, the drill scope key, the columns
// it renders, and how to extract the drill value from a row. metaTypes for
// metadata buckets share the same Risk + Click rate columns; course and
// user have their own column shape since their data fields differ.
const TYPE_META = {
    // 'source' getters read the live ./data bindings at access time so the
    // tables refresh after each hydration.
    department: { label: 'Departments', singular: 'Department', get source() { return DEPTS; },     drillType: 'department', drillValue: (r) => r.name },
    location:   { label: 'Locations',   singular: 'Location',   get source() { return LOCS; },      drillType: 'location',   drillValue: (r) => r.name },
    city:       { label: 'Cities',      singular: 'City',       get source() { return CITIES; },    drillType: 'city',       drillValue: (r) => r.name },
    country:    { label: 'Countries',   singular: 'Country',    get source() { return COUNTRIES; }, drillType: 'country',    drillValue: (r) => r.name },
    company:    { label: 'Companies',   singular: 'Company',    get source() { return COMPANIES; }, drillType: 'company',    drillValue: (r) => r.name },
    state:      { label: 'States',      singular: 'State',      get source() { return STATES; },    drillType: 'state',      drillValue: (r) => r.name },
    manager:    { label: 'Line managers', singular: 'Line manager', get source() { return managers(); },   drillType: 'manager',  drillValue: (r) => r.name },
    commsType:  { label: 'Comms types',   singular: 'Comms type',   get source() { return commsTypes(); }, drillType: 'commsType', drillValue: (r) => r.name },
    course:     { label: 'Courses',     singular: 'Course',     get source() { return COURSES; },   drillType: 'course',     drillValue: (r) => r.name },
    user:       { label: 'Users',       singular: 'User',       get source() { return USERS; },     drillType: 'user',       drillValue: (r) => r.email || r.name },
};

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.04 } } };

// Which Browse-by chips to hide when pivoting from a particular page. Mirrors
// the exclude lists each consuming page passes to MetaNav directly.
const PAGE_EXCLUDE = {
    summary:           ['course'],
    sims:              ['course'],
    'send-history':    ['course'],
    'high-risk-users': ['course'],
};

// Sort options vary per type: metadata buckets sort by Risk/Click rate,
// courses by Sent/Completion, users by Risk/Clicks.
const SORTS_BY_TYPE = {
    department: [
        { id: 'risk',  label: 'Risk',       getter: (r) => r.risk },
        { id: 'click', label: 'Click rate', getter: (r) => r.click },
        { id: 'name',  label: 'Name',       getter: (r) => r.name },
    ],
    location:   null, city: null, country: null, company: null, state: null,
    course: [
        { id: 'sent',      label: 'Sent',         getter: (r) => r.sent },
        { id: 'pct',       label: 'Completion %', getter: (r) => r.pct },
        { id: 'completed', label: 'Completed',    getter: (r) => r.completed },
        { id: 'name',      label: 'Name',         getter: (r) => r.name },
    ],
    user: [
        { id: 'risk',   label: 'Risk',   getter: (r) => r.risk },
        { id: 'clicks', label: 'Clicks', getter: (r) => r.clicks },
        { id: 'comp',   label: 'Completion', getter: (r) => r.comp },
        { id: 'name',   label: 'Name',   getter: (r) => r.name },
    ],
};
// Reuse the metadata-bucket sort list for every dimension that shares its shape.
['location', 'city', 'country', 'company', 'state', 'manager', 'commsType'].forEach((k) => {
    SORTS_BY_TYPE[k] = SORTS_BY_TYPE.department;
});

function RiskBadge({ value }) {
    const lv = value >= 75 ? 'high' : value >= 55 ? 'med' : 'low';
    const c = riskColor(lv);
    return (
        <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '999px', fontSize: 10, fontWeight: 600, bgcolor: c.bg, color: c.text }}>
            {value}
        </Box>
    );
}

export default function MetaListPage({ drill, metaType, originPage, scope, period, startDate, endDate }) {
    const range = { period, startDate, endDate };
    // When MetaNav applies multiple dimensions in one go, metaType arrives as
    // an array. Render one table per dimension stacked vertically; the
    // original single-string contract is preserved by wrapping a scalar
    // value in a one-element array before fan-out.
    const types = Array.isArray(metaType) ? metaType : [metaType];
    if (types.length > 1) {
        return (
            <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {types.map((t) => (
                    <MetaTable key={t} drill={drill} metaType={t} originPage={originPage} scope={scope} range={range} />
                ))}
            </motion.div>
        );
    }
    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <MetaTable drill={drill} metaType={types[0]} originPage={originPage} scope={scope} range={range} />
        </motion.div>
    );
}

// Dimensions served by the rollups (leagues = users/risk/bands, sim_dims = click rate,
// course_dims = completion). manager/commsType/course/user stay on the client data layer.
const SERVER_DIMS = new Set(['department', 'location', 'city', 'country', 'company', 'state']);

function MetaTable({ drill, metaType, originPage, scope, range = {} }) {
    const theme = useTheme();

    const meta = TYPE_META[metaType];
    const isServerDim = SERVER_DIMS.has(metaType);
    // The rollups this table reads (leagues / sim_dims / course_dims) don't see
    // the client-pooled ./data, so a Tenancy filter is forwarded to the server
    // to pool the selected child tenancies. metaType stays the pivot dimension.
    const tenancyScope = Array.isArray(scope?.tenancy) && scope.tenancy.length ? { tenancy: scope.tenancy } : undefined;
    const leaguesQ = useDashboardData({ metric: 'simulations', aggregation: 'leagues', scope: tenancyScope }, { skip: !isServerDim });
    // Click rate and completion follow the selected period (BD-1050); users and risk stay
    // on the fixed 6-month risk league.
    const simDimsQ = useDashboardData({ metric: 'simulations', aggregation: 'sim_dims', groupBy: metaType, ...range, scope: tenancyScope }, { skip: !isServerDim });
    const courseDimsQ = useDashboardData({ metric: 'courses', aggregation: 'course_dims', groupBy: metaType, ...range, scope: tenancyScope }, { skip: !isServerDim });
    // Local rows (loading fallback and the client-only types) re-derive their period
    // figures from the windowed logs.
    const localSource = React.useMemo(() => (meta ? windowedSource(metaType, meta.source, range) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [meta, metaType, range.period, range.startDate, range.endDate, DATA_EPOCH]);
    const serverSource = React.useMemo(() => {
        if (!isServerDim) return null;
        if (!Array.isArray(leaguesQ.data?.leagues)) return tenancyScope ? [] : null;
        const base = leaguesQ.data.leagues.filter((r) => r.dimType === metaType);
        if (!base.length) return tenancyScope ? [] : null;
        const clicks = new Map((simDimsQ.data?.simDims || []).map((r) => [r.dimValue, r]));
        const comp = new Map();
        for (const r of courseDimsQ.data?.courseDims || []) {
            const x = comp.get(r.dimValue) || { sent: 0, completed: 0 };
            x.sent += r.sent; x.completed += r.completed; comp.set(r.dimValue, x);
        }
        return base.map((r) => {
            const ck = clicks.get(r.name); const cp = comp.get(r.name);
            return {
                name: r.name, users: r.users, risk: Math.round(r.avgRisk),
                click: ck && ck.sent > 0 ? Math.round((ck.clicks / ck.sent) * 100) : 0,
                completion: cp && cp.sent > 0 ? Math.round((cp.completed / cp.sent) * 100) : 0,
                lv: r.high > r.low ? 'high' : r.med > r.low ? 'med' : 'low',
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isServerDim, metaType, leaguesQ.data, simDimsQ.data, courseDimsQ.data, !!tenancyScope]);
    const sorts = SORTS_BY_TYPE[metaType] || SORTS_BY_TYPE.department;
    const [sortId, setSortId] = useState(sorts[0].id);
    const [sortDir, setSortDir] = useState('desc');

    // useMemo MUST run before any early return so hook call order stays
    // stable across renders, even when metaType is unknown.
    const sorted = useMemo(() => {
        if (!meta) return [];
        const sort = sorts.find((s) => s.id === sortId) || sorts[0];
        const dir = sortDir === 'desc' ? -1 : 1;
        return [...(serverSource || localSource)].sort((a, b) => {
            const av = sort.getter(a);
            const bv = sort.getter(b);
            if (typeof av === 'string') return av.localeCompare(bv) * dir;
            return (av - bv) * dir;
        });
    }, [meta, sortId, sortDir, sorts, serverSource, localSource]);

    if (!meta) {
        return (
            <Box sx={{ px: 3, py: 6, textAlign: 'center', color: 'text.secondary', fontSize: 12 }}>
                Unknown metadata type: {String(metaType)}
            </Box>
        );
    }

    const toggleSort = (id) => {
        if (id === sortId) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        else { setSortId(id); setSortDir(id === 'name' ? 'asc' : 'desc'); }
    };

    const TH = ({ id, children, align = 'left' }) => (
        <Box
            component="th"
            onClick={() => toggleSort(id)}
            sx={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: sortId === id ? 'text.primary' : 'text.disabled',
                px: 2, py: 1.5,
                textAlign: align,
                borderBottom: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                bgcolor: 'background.paper',
                '&:hover': { color: 'text.primary' },
            }}
        >
            {children}
            {sortId === id && (
                <Box component="span" sx={{ ml: 0.5, color: 'text.disabled' }}>
                    {sortDir === 'desc' ? '↓' : '↑'}
                </Box>
            )}
        </Box>
    );

    return (
        <motion.div variants={fadeUp}>
            <WidgetState queries={[leaguesQ, simDimsQ, courseDimsQ]}>
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                    <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                                All {meta.label}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                Click View for its scoped report
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                            {meta.source.length} {meta.source.length === 1 ? meta.singular.toLowerCase() : meta.label.toLowerCase()}
                        </Typography>
                    </Box>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', overflow: 'hidden', mx: 3, mb: 3 }}>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <Box component="thead">
                                    <Box component="tr">
                                        <Box component="th" aria-hidden="true" sx={{ borderBottom: '1px solid', borderColor: 'divider', width: '1px' }} />
                                        <TH id="name">{meta.singular}</TH>
                                        {metaType === 'course' && (<>
                                            <TH id="sent">Sent</TH>
                                            <TH id="completed">Completed</TH>
                                            <TH id="pct">Completion %</TH>
                                        </>)}
                                        {metaType === 'user' && (<>
                                            <TH id="dept">Department</TH>
                                            <TH id="risk">Risk</TH>
                                            <TH id="clicks">Clicks</TH>
                                            <TH id="comp">Completion</TH>
                                        </>)}
                                        {!['course', 'user'].includes(metaType) && (<>
                                            <TH id="risk">Risk</TH>
                                            <TH id="click">Click rate</TH>
                                        </>)}
                                    </Box>
                                </Box>
                                <Box component="tbody">
                                    {sorted.map((row) => (
                                        <Box
                                            component="tr"
                                            key={meta.drillValue(row)}
                                            sx={{
                                                borderBottom: '1px solid', borderColor: 'divider',
                                                '&:last-child': { borderBottom: 'none' },
                                            }}
                                        >
                                            <Box component="td" sx={{ px: 2, py: 1.5, width: '1px', whiteSpace: 'nowrap' }}>
                                                <RowViewButton onClick={() => drill(originPage?.id || 'sims', row.name, { type: meta.drillType, value: meta.drillValue(row) })} />
                                            </Box>
                                            <Box component="td" sx={{ px: 2, py: 1.5, fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>{row.name}</Box>
                                            {metaType === 'course' && (<>
                                                <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>{row.sent}</Box>
                                                <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>{row.completed}</Box>
                                                <Box component="td" sx={{ px: 2, py: 1.5, fontWeight: 700, color: row.pct >= 80 ? '#66BB6A' : row.pct >= 60 ? '#FFCA28' : '#FF5252' }}>{row.pct}%</Box>
                                            </>)}
                                            {metaType === 'user' && (<>
                                                <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>{row.dept}</Box>
                                                <Box component="td" sx={{ px: 2, py: 1.5 }}><RiskBadge value={row.risk} /></Box>
                                                <Box component="td" sx={{ px: 2, py: 1.5, fontWeight: 700, color: row.clicks > 2 ? '#FF5252' : row.clicks > 0 ? '#FFCA28' : '#66BB6A' }}>{row.clicks}</Box>
                                                <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>{row.comp}%</Box>
                                            </>)}
                                            {!['course', 'user'].includes(metaType) && (<>
                                                <Box component="td" sx={{ px: 2, py: 1.5 }}><RiskBadge value={row.risk} /></Box>
                                                <Box component="td" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>{row.click}%</Box>
                                            </>)}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
            </MainCard>
            </WidgetState>
        </motion.div>
    );
}
